import { useState, useEffect, useMemo } from 'react'
import { implementationStatusApi, type TaskSummary } from '../api/implementationStatus'
import { Page, PageHeader, Card, CardContent, LoadingState, Callout, DataTable, StatusPill, Button, Dialog, Icon } from '../components/ui'

/** Color rule from the task card: green PASS/DONE, yellow PASS_WITH_CONCERNS, gray/blue READY, red FAIL. */
function getTone(value: string): 'success' | 'warning' | 'error' | 'info' | 'neutral' {
  switch (value) {
    case 'PASS':
    case 'DONE':
      return 'success'
    case 'PASS_WITH_CONCERNS':
      return 'warning'
    case 'FAIL':
      return 'error'
    case 'READY':
      return 'info'
    default:
      return 'neutral'
  }
}

export function ImplementationStatusPage() {
  const [items, setItems] = useState<TaskSummary[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const [detailId, setDetailId] = useState<string | null>(null)
  const [detailMarkdown, setDetailMarkdown] = useState('')
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState('')

  useEffect(() => {
    setLoading(true)
    setError('')
    implementationStatusApi.list()
      .then(setItems)
      .catch(e => setError(e instanceof Error ? e.message : 'Errore'))
      .finally(() => setLoading(false))
  }, [])

  const summary = useMemo(() => {
    const total = items.length
    const done = items.filter(t => t.status === 'DONE').length
    const ready = items.filter(t => t.status === 'READY').length
    const other = total - done - ready
    const parts = [`${done} completati`, `${ready} pronti`]
    if (other > 0) parts.push(`${other} altro`)
    return `${total} task totali — ${parts.join(', ')}`
  }, [items])

  function openDetail(id: string) {
    setDetailId(id)
    setDetailMarkdown('')
    setDetailError('')
    setDetailLoading(true)
    implementationStatusApi.getCard(id)
      .then(card => setDetailMarkdown(card.markdown))
      .catch(e => setDetailError(e instanceof Error ? e.message : 'Errore'))
      .finally(() => setDetailLoading(false))
  }

  function closeDetail() {
    setDetailId(null)
  }

  return (
    <Page>
      <PageHeader
        title="Stato implementazione"
        icon="construction"
        subtitle={!loading && !error ? summary : undefined}
      />

      <Card variant="outlined" padding="none">
        <CardContent>
          {loading ? (
            <LoadingState />
          ) : error ? (
            <Callout tone="error">{error}</Callout>
          ) : items.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <Icon name="checklist" size={40} />
              <div>Nessun task registrato</div>
            </div>
          ) : (
            <DataTable variant="plain">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Titolo</th>
                  <th>Epic</th>
                  <th>Stato</th>
                  <th>Test</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {items.map(t => {
                  const badgeValue = t.verdict ?? t.status
                  return (
                    <tr key={t.id}>
                      <td className="text-nowrap md-typescale-emphasized-body-medium">{t.id}</td>
                      <td>{t.title}</td>
                      <td className="text-muted md-typescale-body-small">{t.epic}</td>
                      <td><StatusPill tone={getTone(badgeValue)}>{badgeValue}</StatusPill></td>
                      <td className="text-muted md-typescale-body-small text-nowrap">{t.tests ?? '—'}</td>
                      <td className="text-end">
                        <Button
                          variant="outlined"
                          size="sm"
                          onClick={() => openDetail(t.id)}
                        >
                          Dettagli
                        </Button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </DataTable>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={!!detailId}
        onClose={closeDetail}
        title={detailId ?? ''}
      >
        {detailLoading ? (
          <LoadingState />
        ) : detailError ? (
          <Callout tone="error">{detailError}</Callout>
        ) : (
          <pre className="font-monospace md-typescale-body-small mb-0 overflow-auto md-u-scroll-y-65vh md-u-pre-wrap">
            {detailMarkdown}
          </pre>
        )}
      </Dialog>
    </Page>
  )
}
